import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography, Paper, Fade } from '@mui/material';
import React, { Component, ReactNode, ErrorInfo } from 'react';
import { Link, useRouteError, useNavigate, useResolvedPath } from 'react-router-dom';
import { baseName, Condition, coonditionContext } from '..';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
interface Props {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, ErrorBoundaryState> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }
 resetError = () => this.setState({ hasError: false, error: undefined });
  render() {
    if (this.state.hasError) {
      return (
        <NavigableErrorPage resetError={() => {
          this.resetError();
          // // then navigate
          // window.history.pushState({}, '', '/'); // updates URL
        }}
          title="We are sorry - something went wrong."
          
          errorText={this.state.error?.message ?? 'Unknown error'}

              errorInfo={this.state.errorInfo}
                    
        />
      );
    }

    return this.props.children;
  }
}

interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
}

export const RouteErrorPage: React.FC = () => {
  const error = useRouteError() as RouteError;
  console.error("Route Error:", error);

  return (
    <ErrorPage
      errorText={`Status: ${error?.status} ${error?.statusText} ${error.message ?? ''}`}
    />
  );
};



export const NavigableErrorPage: React.FC<{ errorText: string; title?: string, errorInfo?: React.ErrorInfo,  resetError: () => void }> = ({resetError, errorText, errorInfo,title}) => {
  const navigate = useNavigate();
const rootPath = useResolvedPath('/').pathname;
 // const { condition, baseName } = React.useContext<Condition>(coonditionContext);
  
  return <ErrorPage errorText={errorText} errorInfo = {errorInfo} title={ title} onBack={ () => {
    resetError()
        const rootUrl = `${window.location.origin}/map/${baseName}/`;
     window.location.assign(rootUrl);//navigate('/')
  }} />;
};

// Updated ErrorPage using useNavigate
export const ErrorPage: React.FC<{
  errorText: string;
   errorInfo?: React.ErrorInfo;
  title?: string;
  onBack?: () => void;  // optional callback
}> = ({ title = "Oops! Sorry, an unexpected error has occurred.", errorText, onBack ,errorInfo}) => {

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        overflowY: 'auto',
        textAlign: 'center',
        background: 'linear-gradient(160deg, #EDE8FF 0%, #F8F6FF 40%, #FFFFFF 100%)',
        px: 2,
        py: 4,
      }}
    >
      <Box sx={{ flexGrow: 1 }} />
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 520,
            width: '100%',
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
            borderRadius: 4,
            background: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(173,148,255,0.18)',
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/Logo_Violet.png`}
            alt="Polygon Health"
            sx={{ height: 56, mb: 3, objectFit: 'contain' }}
          />

          {/* Error icon */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EDE8FF 0%, #D6CCFF 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 28, color: '#7C5CFC' }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: '#2D2252', mb: 1.5 }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            sx={{ color: '#6E6690', mb: 4, maxWidth: 380, lineHeight: 1.6 }}
          >
            Don't worry — this is just a temporary issue. Please try again or head back to the map.
          </Typography>

          {/* Back button */}
          <Button
            onClick={onBack ?? (() => window.location.assign('/'))}
            variant="contained"
            size="large"
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              borderRadius: '12px',
              px: 4,
              py: 1.4,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #7C5CFC 0%, #9B7FFF 100%)',
              boxShadow: '0 4px 14px rgba(124,92,252,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6A4AEA 0%, #8B6FEF 100%)',
                boxShadow: '0 6px 20px rgba(124,92,252,0.45)',
              },
            }}
          >
            Back to Map
          </Button>
        </Paper>
      </Fade>

      {/* Developer Info Accordion */}
      {(errorInfo || errorText) && (
        <Accordion
          sx={{
            maxWidth: 520,
            width: '100%',
            mt: 3,
            borderRadius: '12px !important',
            background: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(173,148,255,0.12)',
            boxShadow: 'none',
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#9B7FFF' }} />}
            sx={{ px: 3 }}
          >
            <Typography variant="subtitle2" sx={{ color: '#6E6690', fontWeight: 500 }}>
              Developer Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Box
              sx={{
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                fontFamily: '"Roboto Mono", monospace',
                fontSize: '0.8rem',
                color: '#4A4066',
                background: 'rgba(237,232,255,0.4)',
                borderRadius: 2,
                p: 2,
                maxHeight: '40vh',
                overflowY: 'auto',
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: 'inherit' }}>
                {errorText}
              </Typography>
              {errorInfo?.componentStack && (
                <>
                  <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, fontFamily: 'inherit', fontSize: 'inherit', color: 'inherit' }}>
                    Component Stack:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: 'inherit' }}>
                    {errorInfo.componentStack}
                  </Typography>
                </>
              )}
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'inherit', fontSize: 'inherit', color: 'inherit' }}>
                Current URL: {window.location.href}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      <Box sx={{ minHeight: 32 }} />
    </Box>
  );
};

export default ErrorBoundary;

